import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CreditCard, Lock, Loader2, XCircle } from 'lucide-react';
import paymentService from '../services/paymentService';
import Swal from 'sweetalert2';

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('idle'); // idle, creating-session, ready, processing, error
  const [checkoutLoaded, setCheckoutLoaded] = useState(false);
  const [checkoutConfigured, setCheckoutConfigured] = useState(false);
  const embedTargetRef = useRef(null);
  const scriptLoadedRef = useRef(false);
  
  // Get booking data from location.state or sessionStorage
  const [bookingData, setBookingData] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [amount, setAmount] = useState(0);
  const [customerInfo, setCustomerInfo] = useState({});

  useEffect(() => {
    // Get booking data from location.state (passed from BusBooking) or sessionStorage
    const stateData = location.state;
    let bookingDataToUse = null;
    let orderIdToUse = null;
    let amountToUse = 0;
    let customerInfoToUse = {};

    if (stateData && stateData.bookingData) {
      bookingDataToUse = stateData.bookingData;
      orderIdToUse = stateData.orderId;
      amountToUse = stateData.bookingData.totalAmount;
      customerInfoToUse = {
        name: stateData.bookingData.passengerDetails.name,
        email: stateData.bookingData.passengerDetails.email,
        phone: stateData.bookingData.passengerDetails.phone
      };
      
      // Store in sessionStorage as backup
      sessionStorage.setItem('pendingBookingData', JSON.stringify(bookingDataToUse));
      sessionStorage.setItem('pendingPayment', JSON.stringify({
        orderId: orderIdToUse,
        amount: amountToUse,
        currency: 'LKR',
        timestamp: Date.now()
      }));
    } else {
      // Try to get from sessionStorage
      const storedBookingData = sessionStorage.getItem('pendingBookingData');
      const storedPayment = sessionStorage.getItem('pendingPayment');
      
      if (storedBookingData && storedPayment) {
        try {
          bookingDataToUse = JSON.parse(storedBookingData);
          const paymentData = JSON.parse(storedPayment);
          orderIdToUse = paymentData.orderId;
          amountToUse = paymentData.amount || bookingDataToUse.totalAmount;
          customerInfoToUse = {
            name: bookingDataToUse.passengerDetails.name,
            email: bookingDataToUse.passengerDetails.email,
            phone: bookingDataToUse.passengerDetails.phone
          };
        } catch (e) {
          console.error('Error parsing stored booking data:', e);
          Swal.fire({
            icon: 'error',
            title: 'Booking Data Not Found',
            text: 'Please start your booking again.',
            confirmButtonColor: '#dc2626'
          }).then(() => {
            navigate('/book');
          });
          return;
        }
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Booking Data Not Found',
          text: 'Please start your booking again.',
          confirmButtonColor: '#dc2626'
        }).then(() => {
          navigate('/book');
        });
        return;
      }
    }

    // Set state for display
    setBookingData(bookingDataToUse);
    setOrderId(orderIdToUse);
    setAmount(amountToUse);
    setCustomerInfo(customerInfoToUse);

    // Clear any previous payment session data to avoid conflicts
    paymentService.clearStoredBookingData();
    
    // Reset states
    setSessionId(null);
    setCheckoutLoaded(false);
    setCheckoutConfigured(false);
    scriptLoadedRef.current = false;
    
    // Initialize payment when component mounts - use direct values, not state
    if (bookingDataToUse && amountToUse > 0 && orderIdToUse) {
      initializePayment(orderIdToUse, amountToUse, customerInfoToUse);
    }
  }, []); // Run only once on mount

  // Load Mastercard Checkout Script
  const loadCheckoutScript = () => {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (window.Checkout && scriptLoadedRef.current) {
        setCheckoutLoaded(true);
        resolve(window.Checkout);
        return;
      }

      // Check if script tag already exists
      const existingScript = document.querySelector('script[src*="checkout.min.js"]');
      if (existingScript) {
        // Script tag exists, wait for it to load
        if (window.Checkout) {
          scriptLoadedRef.current = true;
          setCheckoutLoaded(true);
          resolve(window.Checkout);
          return;
        }
        // Wait a bit for script to load
        const checkInterval = setInterval(() => {
          if (window.Checkout) {
            clearInterval(checkInterval);
            scriptLoadedRef.current = true;
            setCheckoutLoaded(true);
            resolve(window.Checkout);
          }
        }, 100);
        
        setTimeout(() => {
          clearInterval(checkInterval);
          if (!window.Checkout) {
            reject(new Error('Checkout script timeout'));
          }
        }, 10000);
        return;
      }

      // Create script element
      const script = document.createElement('script');
      script.src = 'https://bankofceylon.gateway.mastercard.com/static/checkout/checkout.min.js';
      
      // Define callbacks before loading script
      window.errorCallback = (error) => {
        console.error('Mastercard Checkout Error:', error);
        setPaymentStatus('error');
        setLoading(false);
        
        Swal.fire({
          icon: 'error',
          title: 'Payment Error',
          text: error.explanation || error.message || 'An error occurred during payment processing.',
          confirmButtonColor: '#dc2626'
        }).then(() => {
          navigate('/book');
        });
      };

      window.cancelCallback = () => {
        setPaymentStatus('ready');
        setLoading(false);
        
        Swal.fire({
          icon: 'info',
          title: 'Payment Cancelled',
          text: 'You cancelled the payment process.',
          confirmButtonColor: '#6b7280'
        }).then(() => {
          navigate('/book');
        });
      };

      window.successCallback = (result) => {
        setPaymentStatus('success');
        setLoading(false);
        
        // Get current orderId and amount from state or use from result
        const currentOrderId = orderId || result.orderId;
        const currentAmount = amount || 0;
        
        // Store payment result
        sessionStorage.setItem('paymentResult', JSON.stringify({
          success: true,
          orderId: currentOrderId,
          transactionId: result.transactionId || result.orderId || result.resultIndicator,
          resultIndicator: result.resultIndicator,
          amount: currentAmount,
          currency: 'LKR'
        }));
        
        // Redirect to payment callback page which will handle receipt download
        navigate('/payment/callback', {
          state: {
            paymentResult: {
              success: true,
              orderId: currentOrderId,
              transactionId: result.transactionId || result.orderId || result.resultIndicator,
              resultIndicator: result.resultIndicator
            }
          }
        });
      };

      // Set callback attributes
      script.setAttribute('data-error', 'errorCallback');
      script.setAttribute('data-cancel', 'cancelCallback');
      script.setAttribute('data-complete', 'successCallback');
      
      script.async = true;
      script.onload = () => {
        // Wait a bit for Checkout object to be available
        const checkInterval = setInterval(() => {
          if (window.Checkout) {
            clearInterval(checkInterval);
            scriptLoadedRef.current = true;
            setCheckoutLoaded(true);
            resolve(window.Checkout);
          }
        }, 100);
        
        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          if (!window.Checkout) {
            reject(new Error('Checkout object not found after script load'));
          }
        }, 10000);
      };
      script.onerror = () => {
        reject(new Error('Failed to load Mastercard Checkout script'));
      };
      
      document.head.appendChild(script);
    });
  };

  const initializePayment = async (orderIdParam, amountParam, customerInfoParam) => {
    setLoading(true);
    setPaymentStatus('creating-session');

    try {
      // Use parameters if provided, otherwise fall back to state
      const currentOrderId = orderIdParam || orderId;
      const currentAmount = amountParam || amount;
      const currentCustomerInfo = customerInfoParam || customerInfo;

      if (!currentOrderId || !currentAmount || currentAmount <= 0) {
        throw new Error('Invalid booking data. Please try again.');
      }

      // Load checkout script first
      await loadCheckoutScript();

      // Build return URL for payment callback
      const returnUrl = `${window.location.origin}/payment/callback`;

      // Establish payment session with backend
      const sessionResponse = await paymentService.establishSession(
        currentOrderId,
        currentAmount,
        'LKR',
        currentCustomerInfo,
        returnUrl
      );

      if (!sessionResponse.sessionId) {
        throw new Error('Failed to get payment session ID');
      }

      setSessionId(sessionResponse.sessionId);
      console.log('✅ Session ID received:', sessionResponse.sessionId);

      // Step 2: Configure Checkout with session ID only
      if (window.Checkout) {
        try {
          window.Checkout.configure({
            session: {
              id: sessionResponse.sessionId
            }
          });
          setCheckoutConfigured(true);
        } catch (configError) {
          console.error('Error configuring checkout:', configError);
          throw new Error('Failed to configure checkout: ' + configError.message);
        }
      } else {
        throw new Error('Checkout object not available');
      }

      setPaymentStatus('ready');
      setLoading(false);

      console.log('Payment session established:', {
        sessionId: sessionResponse.sessionId,
        orderId: currentOrderId,
        amount: currentAmount
      });

      // Step 3: Show embedded page after ensuring DOM is ready
      // Use longer delay to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        setTimeout(() => {
          console.log('Attempting to show embedded checkout...');
          showEmbeddedCheckout(sessionResponse.sessionId);
        }, 1000); // Increased delay to 1 second
      });
      
    } catch (error) {
      console.error('Error initializing payment:', error);
      setPaymentStatus('error');
      setLoading(false);
      
      const errorMessage = error.message || 'Failed to initialize payment. Please try again.';
      
      Swal.fire({
        icon: 'error',
        title: 'Payment Initialization Failed',
        html: `<div style="text-align: left;">
          <p><strong>Error:</strong> ${errorMessage}</p>
          <p style="font-size: 12px; color: #666; margin-top: 10px;">
            Please check the browser console (F12) for more details.
          </p>
        </div>`,
        confirmButtonColor: '#dc2626',
        width: '500px'
      }).then(() => {
        navigate('/book');
      });
    }
  };

  // Show Embedded Checkout Page
  const showEmbeddedCheckout = (sessionIdToUse = null) => {
    const currentSessionId = sessionIdToUse || sessionId;
    const embedElement = document.getElementById('embed-target');
    
    if (!window.Checkout) {
      console.error('Checkout object not available, retrying...');
      setTimeout(() => {
        showEmbeddedCheckout(currentSessionId);
      }, 500);
      return;
    }

    if (!currentSessionId) {
      console.error('Session ID not available');
      if (sessionId) {
        setTimeout(() => {
          showEmbeddedCheckout(sessionId);
        }, 500);
      }
      return;
    }

    if (!embedElement) {
      console.error('Embed target element not found in DOM, retrying...');
      setTimeout(() => {
        showEmbeddedCheckout(currentSessionId);
      }, 500);
      return;
    }

    try {
      setPaymentStatus('processing');
      setLoading(false);

      // Store booking data before showing checkout
      if (bookingData) {
        sessionStorage.setItem('pendingBookingData', JSON.stringify(bookingData));
        sessionStorage.setItem('pendingPayment', JSON.stringify({
          orderId: orderId || currentSessionId,
          amount: amount || 0,
          currency: 'LKR',
          sessionId: currentSessionId,
          timestamp: Date.now()
        }));
      }

      // Show embedded checkout page
      console.log('Calling showEmbeddedPage with selector: #embed-target');
      console.log('Embed element exists:', !!embedElement);
      console.log('Embed element dimensions:', {
        width: embedElement?.offsetWidth,
        height: embedElement?.offsetHeight,
        visible: embedElement?.offsetParent !== null
      });
      console.log('Session ID:', currentSessionId);
      console.log('Checkout object:', window.Checkout);
      
      // Clear any previous content in embed target
      embedElement.innerHTML = '';
      
      // Note: Cookie warnings (PIM-SESSION-ID) are normal and can be ignored
      // They're browser security warnings but don't prevent checkout from working
      
      // Ensure embed element is visible and has proper dimensions
      embedElement.style.display = 'block';
      embedElement.style.visibility = 'visible';
      embedElement.style.minHeight = '550px';
      
      window.Checkout.showEmbeddedPage('#embed-target');
      
      console.log('showEmbeddedPage called successfully');
      
      // Check if content was rendered after a delay
      setTimeout(() => {
        // Check for actual Mastercard content (iframe or form elements)
        const hasIframe = embedElement.querySelector('iframe') !== null;
        const hasMastercardContent = embedElement.querySelector('form') !== null ||
                                    embedElement.querySelector('[class*="checkout"]') !== null ||
                                    embedElement.querySelector('[id*="checkout"]') !== null;
        const hasContent = hasIframe || hasMastercardContent;
        
        console.log('Embed target content check:', {
          hasChildren: embedElement.children.length > 0,
          hasInnerHTML: embedElement.innerHTML.trim().length > 0,
          hasIframe: hasIframe,
          hasMastercardContent: hasMastercardContent,
          hasContent: hasContent,
          childCount: embedElement.children.length,
          innerHTML: embedElement.innerHTML.substring(0, 200) // First 200 chars for debugging
        });
        
        if (hasContent) {
          console.log('✅ Embedded checkout page loaded successfully!');
          // Hide any loading spinners by ensuring processing state
          setPaymentStatus('processing');
        } else {
          console.warn('⚠️ Embedded checkout page may not have loaded. Checking again...');
          // Retry after another delay
          setTimeout(() => {
            const retryHasIframe = embedElement.querySelector('iframe') !== null;
            const retryHasMastercardContent = embedElement.querySelector('form') !== null ||
                                             embedElement.querySelector('[class*="checkout"]') !== null;
            const retryHasContent = retryHasIframe || retryHasMastercardContent;
            
            if (retryHasContent) {
              console.log('✅ Embedded checkout page loaded on retry!');
              setPaymentStatus('processing');
            } else {
              console.error('❌ Embedded checkout page did not load after retry');
              console.error('Element state:', {
                innerHTML: embedElement.innerHTML.substring(0, 500),
                children: Array.from(embedElement.children).map(c => c.tagName)
              });
              // Don't set error immediately - Mastercard might still be loading
              // Just log the warning
            }
          }, 3000);
        }
      }, 2000);

    } catch (error) {
      console.error('Error showing embedded checkout:', error);
      console.error('Error stack:', error.stack);
      setPaymentStatus('error');
      setLoading(false);
      
      Swal.fire({
        icon: 'error',
        title: 'Payment Error',
        html: `<div style="text-align: left;">
          <p><strong>Error:</strong> ${error.message || 'Failed to load payment form.'}</p>
          <p style="font-size: 12px; color: #666; margin-top: 10px;">
            Please check the browser console (F12) for more details.
          </p>
        </div>`,
        confirmButtonColor: '#dc2626',
        width: '500px'
      }).then(() => {
        navigate('/book');
      });
    }
  };

  const formatAmount = (amt) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amt);
  };

  if (!bookingData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CreditCard className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Complete Payment</h1>
                <p className="text-sm text-gray-500">Secure payment via Mastercard</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/book')}
              className="text-gray-400 hover:text-gray-600 transition-colors px-3 py-2 rounded-lg hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Payment Amount */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <span className="text-gray-600 text-lg">Total Amount</span>
            <span className="text-3xl font-bold text-gray-900">
              {formatAmount(amount)}
            </span>
          </div>
          <div className="mt-2 space-y-1">
            {/* Order ID and Session ID hidden from user view - only logged in console for debugging */}
          </div>
        </div>

        {/* Payment Status - Error */}
        {paymentStatus === 'error' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-3">
              <XCircle className="h-6 w-6 text-red-600" />
              <div>
                <p className="font-semibold text-red-900">Payment Failed</p>
                <p className="text-sm text-red-700">Please try again or use a different payment method.</p>
              </div>
            </div>
          </div>
        )}

        {/* Embedded Checkout Container - Always render so DOM element exists */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {loading && paymentStatus === 'creating-session' && (
            <div className="text-center py-12">
              <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600 text-lg">Loading secure payment form...</p>
            </div>
          )}
          
          {(paymentStatus === 'ready' || paymentStatus === 'processing') && (
            <div className="mb-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Lock className="h-4 w-4" />
                <span>Enter your payment details below</span>
              </div>
            </div>
          )}
          
          {/* Mastercard Embedded Checkout will be rendered here - Always render DOM element */}
          <div 
            id="embed-target" 
            ref={embedTargetRef}
            className="w-full border border-gray-200 rounded-lg bg-white p-4"
            style={{ 
              minHeight: '550px', 
              maxHeight: '600px', 
              overflowY: 'auto',
              position: 'relative',
              backgroundColor: '#ffffff'
            }}
          >
            {/* Only show loading spinner if checkout hasn't been called yet */}
            {paymentStatus === 'creating-session' && (
              <div className="flex items-center justify-center h-full" style={{ minHeight: '500px' }}>
                <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                <span className="ml-3 text-gray-600">Creating payment session...</span>
              </div>
            )}
            {paymentStatus === 'ready' && !checkoutConfigured && (
              <div className="flex items-center justify-center h-full" style={{ minHeight: '500px' }}>
                <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                <span className="ml-3 text-gray-600">Configuring payment form...</span>
              </div>
            )}
            {/* Don't show spinner when processing - let Mastercard's content show */}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
            <Lock className="h-3 w-3" />
            <span>Secured by Mastercard Payment Gateway</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Your card details are encrypted and secure
          </p>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
